import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/WeatherDashboardAdvanced.css';

interface WeatherData {
  current: {
    temp: number;
    feelsLike: number;
    humidity: number;
    windSpeed: number;
    windDirection: number;
    pressure: number;
    visibility: number;
    uvIndex: number;
    condition: string;
    icon: string;
    sunrise: string;
    sunset: string;
  };
  forecast: ForecastDay[];
  hourly: HourlyForecast[];
  alerts: WeatherAlert[];
}

interface ForecastDay {
  date: string;
  high: number;
  low: number;
  condition: string;
  icon: string;
  precipitation: number;
  windSpeed: number;
}

interface HourlyForecast {
  time: string;
  temp: number;
  condition: string;
  icon: string;
  precipitation: number;
}

interface WeatherAlert {
  id: string;
  type: 'warning' | 'watch' | 'advisory';
  title: string;
  description: string;
  severity: 'minor' | 'moderate' | 'severe' | 'extreme';
  start: string;
  end: string;
}

interface Location {
  name: string;
  country: string;
  lat: number;
  lon: number;
  timezone: string;
}

const WeatherDashboardAdvanced: React.FC = () => {
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [currentLocation, setCurrentLocation] = useState<Location>({
    name: 'New York',
    country: 'United States',
    lat: 40.7128,
    lon: -74.0060,
    timezone: 'America/New_York'
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'current' | 'forecast' | 'radar' | 'alerts'>('current');
  const [tempUnit, setTempUnit] = useState<'celsius' | 'fahrenheit'>('fahrenheit');
  const [favoriteLocations, setFavoriteLocations] = useState<Location[]>([
    { name: 'London', country: 'United Kingdom', lat: 51.5074, lon: -0.1278, timezone: 'Europe/London' },
    { name: 'Tokyo', country: 'Japan', lat: 35.6762, lon: 139.6503, timezone: 'Asia/Tokyo' },
    { name: 'Sydney', country: 'Australia', lat: -33.8688, lon: 151.2093, timezone: 'Australia/Sydney' }
  ]);

  // Mock weather data (in real app this would come from API)
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const mockData: WeatherData = {
        current: {
          temp: 72,
          feelsLike: 75,
          humidity: 68,
          windSpeed: 12,
          windDirection: 220,
          pressure: 30.15,
          visibility: 10,
          uvIndex: 6,
          condition: 'Partly Cloudy',
          icon: '⛅',
          sunrise: '6:42 AM',
          sunset: '7:28 PM'
        },
        forecast: [
          { date: 'Today', high: 78, low: 65, condition: 'Partly Cloudy', icon: '⛅', precipitation: 20, windSpeed: 12 },
          { date: 'Tomorrow', high: 82, low: 68, condition: 'Sunny', icon: '☀️', precipitation: 5, windSpeed: 8 },
          { date: 'Wednesday', high: 75, low: 62, condition: 'Thunderstorms', icon: '⛈️', precipitation: 85, windSpeed: 18 },
          { date: 'Thursday', high: 71, low: 58, condition: 'Rain', icon: '🌧️', precipitation: 70, windSpeed: 15 },
          { date: 'Friday', high: 77, low: 63, condition: 'Partly Cloudy', icon: '⛅', precipitation: 25, windSpeed: 10 },
          { date: 'Saturday', high: 80, low: 66, condition: 'Sunny', icon: '☀️', precipitation: 10, windSpeed: 9 },
          { date: 'Sunday', high: 83, low: 69, condition: 'Sunny', icon: '☀️', precipitation: 5, windSpeed: 11 }
        ],
        hourly: Array.from({ length: 24 }, (_, i) => ({
          time: `${i}:00`,
          temp: 72 + Math.sin(i / 4) * 8,
          condition: i < 6 || i > 18 ? 'Clear' : 'Partly Cloudy',
          icon: i < 6 || i > 18 ? '🌙' : '⛅',
          precipitation: Math.random() * 30
        })),
        alerts: [
          {
            id: '1',
            type: 'warning',
            title: 'Heat Advisory',
            description: 'Temperatures may reach dangerous levels. Stay hydrated and avoid prolonged sun exposure.',
            severity: 'moderate',
            start: '2024-01-15T12:00:00Z',
            end: '2024-01-15T20:00:00Z'
          }
        ]
      };
      setWeatherData(mockData);
      setLoading(false);
    }, 1500);
  }, [currentLocation]);

  const convertTemp = (temp: number) => {
    if (tempUnit === 'celsius') {
      return Math.round((temp - 32) * 5/9);
    }
    return Math.round(temp);
  };

  const getWindDirection = (degrees: number) => {
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    return directions[Math.round(degrees / 22.5) % 16];
  };

  const getUVLevel = (index: number) => {
    if (index <= 2) return { level: 'Low', color: '#4CAF50' };
    if (index <= 5) return { level: 'Moderate', color: '#FFA726' };
    if (index <= 7) return { level: 'High', color: '#FF7043' };
    if (index <= 10) return { level: 'Very High', color: '#E53935' };
    return { level: 'Extreme', color: '#8E24AA' };
  };

  const getAlertColor = (severity: WeatherAlert['severity']) => {
    switch (severity) {
      case 'minor': return '#FFA726';
      case 'moderate': return '#FF7043';
      case 'severe': return '#E53935';
      case 'extreme': return '#8E24AA';
      default: return '#757575';
    }
  };

  if (loading) {
    return (
      <div className="weather-loading">
        <div className="loading-spinner"></div>
        <p>Loading weather data...</p>
      </div>
    );
  }

  return (
    <div className="weather-dashboard-advanced">
      <div className="weather-header">
        <div className="header-left">
          <Link to="/" className="back-button-weather">← Back to Home</Link>
          <div className="title-section">
            <h1>🌤️ Weather Command Center</h1>
            <div className="version-badge-weather v15">Synoppy 1.5</div>
          </div>
        </div>
        
        <div className="header-controls">
          <div className="search-location">
            <input
              type="text"
              placeholder="Search locations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="location-search"
            />
            <button className="search-btn">🔍</button>
          </div>
          
          <div className="unit-toggle">
            <button 
              className={tempUnit === 'fahrenheit' ? 'active' : ''}
              onClick={() => setTempUnit('fahrenheit')}
            >°F</button>
            <button 
              className={tempUnit === 'celsius' ? 'active' : ''}
              onClick={() => setTempUnit('celsius')}
            >°C</button>
          </div>
        </div>
      </div>

      <div className="location-info">
        <div className="current-location">
          <h2>{currentLocation.name}, {currentLocation.country}</h2>
          <p>{new Date().toLocaleString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })}</p>
        </div>
        
        <div className="favorite-locations">
          {favoriteLocations.map((location, index) => (
            <button
              key={index}
              className="location-chip"
              onClick={() => setCurrentLocation(location)}
            >
              {location.name}
            </button>
          ))}
        </div>
      </div>

      <div className="view-mode-tabs">
        <button 
          className={viewMode === 'current' ? 'active' : ''}
          onClick={() => setViewMode('current')}
        >
          Current Weather
        </button>
        <button 
          className={viewMode === 'forecast' ? 'active' : ''}
          onClick={() => setViewMode('forecast')}
        >
          7-Day Forecast
        </button>
        <button 
          className={viewMode === 'radar' ? 'active' : ''}
          onClick={() => setViewMode('radar')}
        >
          Weather Radar
        </button>
        <button 
          className={viewMode === 'alerts' ? 'active' : ''}
          onClick={() => setViewMode('alerts')}
        >
          Alerts {weatherData?.alerts && weatherData.alerts.length > 0 && `(${weatherData.alerts.length})`}
        </button>
      </div>

      {/* Weather Alerts */}
      {weatherData?.alerts && weatherData.alerts.length > 0 && (
        <div className="weather-alerts">
          {weatherData.alerts.map(alert => (
            <div 
              key={alert.id} 
              className="alert-card"
              style={{ borderLeft: `4px solid ${getAlertColor(alert.severity)}` }}
            >
              <div className="alert-header">
                <span className="alert-type">{alert.type.toUpperCase()}</span>
                <span className="alert-severity" style={{ color: getAlertColor(alert.severity) }}>
                  {alert.severity.toUpperCase()}
                </span>
              </div>
              <h3>{alert.title}</h3>
              <p>{alert.description}</p>
            </div>
          ))}
        </div>
      )}

      {viewMode === 'current' && weatherData && (
        <div className="current-weather-section">
          <div className="main-weather-card">
            <div className="current-temp-section">
              <div className="temp-display">
                <span className="current-temp">{convertTemp(weatherData.current.temp)}°</span>
                <span className="weather-icon">{weatherData.current.icon}</span>
              </div>
              <div className="weather-details">
                <p className="condition">{weatherData.current.condition}</p>
                <p className="feels-like">Feels like {convertTemp(weatherData.current.feelsLike)}°</p>
              </div>
            </div>
            
            <div className="weather-metrics">
              <div className="metric-card">
                <div className="metric-icon">💧</div>
                <div className="metric-info">
                  <span className="metric-value">{weatherData.current.humidity}%</span>
                  <span className="metric-label">Humidity</span>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">💨</div>
                <div className="metric-info">
                  <span className="metric-value">{weatherData.current.windSpeed} mph</span>
                  <span className="metric-label">{getWindDirection(weatherData.current.windDirection)} Wind</span>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">🌡️</div>
                <div className="metric-info">
                  <span className="metric-value">{weatherData.current.pressure}"</span>
                  <span className="metric-label">Pressure</span>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">👁️</div>
                <div className="metric-info">
                  <span className="metric-value">{weatherData.current.visibility} mi</span>
                  <span className="metric-label">Visibility</span>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">☀️</div>
                <div className="metric-info">
                  <span className="metric-value">{weatherData.current.uvIndex}</span>
                  <span 
                    className="metric-label"
                    style={{ color: getUVLevel(weatherData.current.uvIndex).color }}
                  >
                    {getUVLevel(weatherData.current.uvIndex).level} UV
                  </span>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">🌅</div>
                <div className="metric-info">
                  <span className="metric-value">{weatherData.current.sunrise}</span>
                  <span className="metric-label">Sunrise</span>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">🌇</div>
                <div className="metric-info">
                  <span className="metric-value">{weatherData.current.sunset}</span>
                  <span className="metric-label">Sunset</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hourly-forecast">
            <h3>24-Hour Forecast</h3>
            <div className="hourly-scroll">
              {weatherData.hourly.map((hour, index) => (
                <div key={index} className="hourly-item">
                  <div className="hourly-time">{hour.time}</div>
                  <div className="hourly-icon">{hour.icon}</div>
                  <div className="hourly-temp">{convertTemp(hour.temp)}°</div>
                  <div className="hourly-precipitation">{Math.round(hour.precipitation)}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {viewMode === 'forecast' && weatherData && (
        <div className="forecast-section">
          <div className="forecast-grid">
            {weatherData.forecast.map((day, index) => (
              <div key={index} className="forecast-card">
                <div className="forecast-day">{day.date}</div>
                <div className="forecast-icon">{day.icon}</div>
                <div className="forecast-condition">{day.condition}</div>
                <div className="forecast-temps">
                  <span className="high-temp">{convertTemp(day.high)}°</span>
                  <span className="low-temp">{convertTemp(day.low)}°</span>
                </div>
                <div className="forecast-details">
                  <div className="detail-item">
                    <span className="detail-icon">🌧️</span>
                    <span>{day.precipitation}%</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">💨</span>
                    <span>{day.windSpeed} mph</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {viewMode === 'radar' && (
        <div className="radar-section">
          <div className="radar-container">
            <div className="radar-map">
              <div className="radar-overlay">
                <div className="radar-center"></div>
                <div className="radar-ring ring-1"></div>
                <div className="radar-ring ring-2"></div>
                <div className="radar-ring ring-3"></div>
                <div className="weather-system system-1"></div>
                <div className="weather-system system-2"></div>
              </div>
            </div>
            <div className="radar-controls">
              <button className="radar-play">▶️ Play</button>
              <button className="radar-layers">🗂️ Layers</button>
              <button className="radar-fullscreen">⛶ Fullscreen</button>
            </div>
          </div>
        </div>
      )}

      <div className="version-comparison-weather">
        <Link to="?version=1.0" className="downgrade-link-weather">
          Compare with Synoppy 1.0 Version →
        </Link>
      </div>

      <div className="weather-footer">
        <div className="feature-showcase">
          <div className="feature">🛰️ Live satellite imagery</div>
          <div className="feature">⚠️ Severe weather alerts</div>
          <div className="feature">📊 Interactive weather maps</div>
          <div className="feature">🌍 Global location support</div>
          <div className="feature">📱 PWA & offline support</div>
          <div className="feature">⏰ Historical weather data</div>
        </div>
      </div>
    </div>
  );
};

export default WeatherDashboardAdvanced;